/* Equity.
   Every chart but RFI is played against a range somebody else arrived with, and the tooltip
   says how each hand fares all-in against it. Which chart holds that range falls out of the
   story the same way hero's prior chart does: the opponent's last move is one action in one
   of its own charts, at the same table for the same money. `via` lists what that move can
   be, the likeliest first; the first one the chart actually plays is the one read.

   The numbers themselves come from data/equity/equity.txt: the exact all-in equity of every
   hand class against every other, generated once by packages/data. A hand's equity against
   a range is the weighted mean over the range's classes, each weighted by how often the
   opponent holds it times how many of its combos do not clash with hero's cards. */

import { FLAT, HAND_AT, LABEL, RANKS, type Action, type WrittenAction } from "./hands";
import { continuingRange } from "./notation";
import { actionLabel } from "./rules";
import { seatName } from "./seats";
import { placeOf, spotKey, type Spot, type SpotIndex } from "./spot";

const N = 169;

/** The class-vs-class table: row i, column j is class i's equity against class j. */
export type EquityTable = Float32Array;

/* The class-vs-class table, decoded. Three base64 digits per pair of the upper triangle, in
   the same 169-hand order the grid is drawn in. Null when the text is not the table. */
export function decodeEquity(raw: unknown): EquityTable | null {
  if (typeof raw !== "string") return null;
  const need = ((N * (N + 1)) / 2) * 3;
  if (raw.length !== need) return null;
  const B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
  const D = new Int8Array(128).fill(-1);
  for (let i = 0; i < 64; i++) D[B64.charCodeAt(i)] = i;
  const digit = (k: number): number => D[raw.charCodeAt(k)] ?? -1;
  const t = new Float32Array(N * N);
  let k = 0;
  for (let i = 0; i < N; i++)
    for (let j = i; j < N; j++, k += 3) {
      const v = ((digit(k) << 12) | (digit(k + 1) << 6) | digit(k + 2)) / 100000;
      t[i * N + j] = v;
      t[j * N + i] = 1 - v;
    }
  return t;
}

/* How many combos of class j are dealable next to one fixed combo of class i: AA leaves AK
   8 of its 16, KK all of them. Card removal, in one small table. */
export const CLASH: Uint8Array = (() => {
  const t = new Uint8Array(N * N);
  const idx: Record<string, number> = Object.fromEntries(RANKS.map((r, i) => [r, i]));
  const cardsOf = (h: string): [number, number][] => {
    const a = idx[h.charAt(0)] ?? 0;
    const b = idx[h.charAt(1)] ?? 0;
    const out: [number, number][] = [];
    if (h.length === 2) {
      for (let s = 0; s < 4; s++) for (let u = s + 1; u < 4; u++) out.push([a * 4 + s, a * 4 + u]);
    } else if (h.charAt(2) === "s") {
      for (let s = 0; s < 4; s++) out.push([a * 4 + s, b * 4 + s]);
    } else {
      for (let s = 0; s < 4; s++)
        for (let u = 0; u < 4; u++) if (s !== u) out.push([a * 4 + s, b * 4 + u]);
    }
    return out;
  };
  const all = FLAT.map(cardsOf);
  all.forEach((mine, i) => {
    const [h1, h2] = mine[0] ?? [-1, -1]; // any combo of i: the deck is suit-blind
    all.forEach((theirs, j) => {
      t[i * N + j] = theirs.filter(
        ([v1, v2]) => v1 !== h1 && v1 !== h2 && v2 !== h1 && v2 !== h2,
      ).length;
    });
  });
  return t;
})();

/** Every class's equity against a weighted range: hand -> 0..1, or null for a hand the
 *  range leaves nothing dealable against. */
export function equityVs(
  eq: EquityTable,
  live: ReadonlyMap<string, number>,
): Map<string, number | null> {
  const out = new Map<string, number | null>();
  const vs: [number, number][] = [];
  for (const [h, w] of live) {
    const at = HAND_AT[h];
    if (w > 0 && at !== undefined) vs.push([at, w]);
  }
  FLAT.forEach((hand, i) => {
    let num = 0;
    let den = 0;
    for (const [j, w] of vs) {
      const n = w * (CLASH[i * N + j] ?? 0);
      if (!n) continue;
      num += n * (eq[i * N + j] ?? 0);
      den += n;
    }
    out.set(hand, den ? num / den : null);
  });
  return out;
}

/** One opponent's last move: whose chart holds it, and which actions in it lead here. */
interface OppMove {
  seat: string;
  action: string;
  seats: string[];
  via: readonly WrittenAction[];
}

/* Which column of the raiser's own chart each answer to a squeeze came out of. A 4-bet and
   a jam are separate answers, so neither stands in for the other: a chart missing the
   column its answer needs has no range to show here rather than the wrong one. */
const SQUEEZE_ANSWER: Readonly<Record<string, readonly WrittenAction[]>> = {
  Called: ["call"],
  "4-Bet": ["raise"],
  Jammed: ["allin"],
};

const OPP_RANGE: Readonly<Record<string, (s: Spot) => OppMove[]>> = {
  "vs. RFI": (s) => [{ seat: s.seats[0] ?? "", action: "RFI", seats: [], via: ["raise"] }],
  "vs. 3 Bet": (s) => [
    { seat: s.seats[0] ?? "", action: "vs. RFI", seats: [s.hero], via: ["raise", "allin"] },
  ],
  "vs. 4 Bet": (s) => [
    { seat: s.seats[0] ?? "", action: "vs. 3 Bet", seats: [s.hero], via: ["raise", "allin"] },
  ],
  "vs. 5 Bet": (s) => [
    { seat: s.seats[0] ?? "", action: "vs. 4 Bet", seats: [s.hero], via: ["allin", "raise"] },
  ],
  "Cold 4bet": ({ seats: [a = "", b = ""] }) => [
    { seat: a, action: "RFI", seats: [], via: ["raise"] },
    { seat: b, action: "vs. RFI", seats: [a], via: ["raise", "allin"] },
  ],
  "vs. Raise-Call": ({ seats: [a = "", b = ""] }) => [
    { seat: a, action: "RFI", seats: [], via: ["raise"] },
    { seat: b, action: "vs. RFI", seats: [a], via: ["call"] },
  ],
  "vs. Squeeze": ({ hero, seats: [a = "", b = ""] }) => [
    { seat: a, action: "vs. RFI", seats: [hero], via: ["call"] },
    { seat: b, action: "vs. Raise-Call", seats: [hero, a], via: ["raise", "allin"] },
  ],
  // The raiser answered the squeeze before hero did, so its range is what it answered with:
  // the column of its own chart that answer came out of. Folded, it is out of the pot and
  // off the tooltip.
  "Called vs. Squeeze": ({ hero, branch, seats: [a = "", b = ""] }) => [
    ...(branch === "Folded"
      ? []
      : [
          {
            seat: a,
            action: "vs. Squeeze",
            seats: [hero, b],
            via: SQUEEZE_ANSWER[branch ?? ""] ?? ["call"],
          },
        ]),
    { seat: b, action: "vs. Raise-Call", seats: [a, hero], via: ["raise", "allin"] },
  ],
};

/** Whether the tooltip has anyone to measure this chart against. */
export const facesSomebody = (action: string): boolean => OPP_RANGE[action] !== undefined;

/** The chart holding one opponent's last move in `spot`, or null. */
function oppChart(index: SpotIndex, spot: Spot, o: OppMove): Spot | null {
  const { branch: _branch, ...place } = placeOf(spot);
  return index.get(spotKey({ ...place, hero: o.seat, action: o.action, seats: o.seats })) ?? null;
}

export type OppRange =
  | { seat: string; missing: string; live?: undefined }
  | { seat: string; chart: Spot; via: Action; label: string; live: Map<string, number> };

/**
 * Each opponent's range in `spot`: {seat, live, chart, via} where `live` maps hand -> how
 * often the opponent holds it, or {seat, missing} naming the chart that is not there. Empty
 * for RFI, which faces nobody.
 */
export function oppRanges(index: SpotIndex, spot: Spot): OppRange[] {
  const rule = OPP_RANGE[spot.action];
  if (!rule) return [];
  return rule(spot).map((o): OppRange => {
    const chart = oppChart(index, spot, o);
    // A chart's name in a sentence, so in the user's names; `seat` stays the id.
    const label = `${seatName(o.seat)} ${actionLabel(o.action)}${o.seats.length ? " vs. " + o.seats.map(seatName).join(", ") : ""}`;
    if (!chart) return { seat: o.seat, missing: label };
    const via = o.via.find((a) => Object.values(chart.cells).some((d) => (d[a] ?? 0) > 0));
    if (!via) return { seat: o.seat, missing: label };
    return { seat: o.seat, chart, via, label, live: continuingRange(chart, 0, [via]) };
  });
}

export interface EquityRow {
  seat: string;
  map: Map<string, number | null>;
}

/** The tooltip's equity lines for one chart: one per opponent whose range could be found.
 *  Nothing without the table. */
export function equityRows(eq: EquityTable | null, index: SpotIndex, spot: Spot): EquityRow[] {
  if (!eq) return [];
  return oppRanges(index, spot).flatMap((o) =>
    o.live ? [{ seat: o.seat, map: equityVs(eq, o.live) }] : [],
  );
}

/** One line under the chart saying what the equities are measured against. */
export function equityNote(eq: EquityTable | null, index: SpotIndex, spot: Spot): string {
  if (!facesSomebody(spot.action)) return "";
  if (!eq) return "The equity table did not load, so the tooltip shows no equity.";
  return oppRanges(index, spot)
    .map((o) =>
      o.live
        ? `Equity vs. ${seatName(o.seat)} is against its ${LABEL[o.via].toLowerCase()} range from the ${o.label} chart.`
        : `No ${o.missing} chart, so there is no equity vs. ${seatName(o.seat)}.`,
    )
    .join(" ");
}
